import { useEffect, useState } from "react";

/**
 * True while the viewport is at most `maxWidth` px — a phone, in practice.
 *
 * Everything in this codebase is styled with inline style objects, and an inline style beats
 * any stylesheet rule on specificity, so a CSS `@media` block cannot reach these components.
 * The breakpoint therefore has to be readable from JS. `matchMedia` IS the browser's own media
 * query engine, so this agrees exactly with a `(max-width: …)` rule rather than approximating
 * one — and it re-renders on rotation and on a desktop window being dragged narrow.
 */
export default function useIsNarrow(maxWidth = 700) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    // addListener is the pre-Safari-14 spelling. This page is opened on whatever phone a
    // client happens to own, so the old one is worth keeping as a fallback.
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [query]);

  return narrow;
}
